import { LogsResponse, SingleLogResponse, JarvisLogEntry } from '../types/jarvis.types';

export class JarvisLogService {
  private static readonly API_URL = 'https://ai-reply-bot.vercel.app/api/logs';

  /**
   * Get a specific log entry by ID
   */
  static async getLogById(requestId: string): Promise<JarvisLogEntry | null> {
    try {
      const response = await fetch(`${this.API_URL}?id=${requestId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: SingleLogResponse = await response.json();
      
      if (result.success && result.data) {
        return result.data;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching log by ID:', error);
      throw error;
    }
  }

  /**
   * Get all logs for a specific date (YYYY-MM-DD format)
   */
  static async getLogsByDate(date: string): Promise<JarvisLogEntry[]> {
    try {
      const response = await fetch(`${this.API_URL}?date=${date}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: LogsResponse = await response.json();
      
      if (result.success && result.data) {
        return result.data;
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching logs by date:', error);
      throw error;
    }
  }

  /**
   * Get recent logs with pagination
   */
  static async getRecentLogs(limit: number = 10, offset: number = 0): Promise<LogsResponse> {
    try {
      const response = await fetch(`${this.API_URL}?limit=${limit}&offset=${offset}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result: LogsResponse = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching recent logs:', error);
      throw error;
    }
  }

  /**
   * Get today's logs
   */
  static async getTodayLogs(): Promise<JarvisLogEntry[]> {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    return this.getLogsByDate(today);
  }

  /**
   * Get logs for the last N days
   */
  static async getLogsForLastNDays(days: number): Promise<Map<string, JarvisLogEntry[]>> {
    const logsByDate = new Map<string, JarvisLogEntry[]>();
    
    const promises = [];
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateString = date.toISOString().split('T')[0];
      promises.push(
        this.getLogsByDate(dateString)
          .then(logs => ({ date: dateString, logs }))
          .catch(() => ({ date: dateString, logs: [] }))
      );
    }

    const results = await Promise.all(promises);
    results.forEach(({ date, logs }) => {
      if (logs.length > 0) {
        logsByDate.set(date, logs);
      }
    });

    return logsByDate;
  }

  /**
   * Calculate total cost for a set of logs
   */
  static calculateTotalCost(logs: JarvisLogEntry[]): number {
    return logs.reduce((total, log) => {
      return total + (log.cost?.total_cost || 0);
    }, 0);
  }

  /**
   * Calculate total tokens for a set of logs
   */
  static calculateTotalTokens(logs: JarvisLogEntry[]): number {
    return logs.reduce((total, log) => {
      return total + (log.tokens?.total_tokens || 0);
    }, 0);
  }

  /**
   * Get the most recent successful log entry
   */
  static getMostRecentSuccess(logs: JarvisLogEntry[]): JarvisLogEntry | null {
    const successfulLogs = logs
      .filter(log => log.status === 'success' && log.response_content)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    
    return successfulLogs.length > 0 ? successfulLogs[0] : null;
  }

  /**
   * Format date to readable string
   */
  static formatDate(dateString: string): string {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const isToday = date.toDateString() === today.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';

    // Format as "Mon, Jan 20"
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  }

  /**
   * Format timestamp to time string
   */
  static formatTime(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  }
}
